import React, { useState } from 'react';
import { Settings, Save } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { getFieldMapping } from '../../../services/erpIntegrationService';

const FieldMappingEditor = ({ integrations, erpSystems }) => {
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [mappings, setMappings] = useState({});

  const handleIntegrationChange = (integrationId) => {
    setSelectedIntegration(integrationId);
    const integration = integrations?.find(i => i?.id === integrationId);
    if (integration) {
      const defaultMapping = getFieldMapping(integration?.erp_system_id);
      setMappings(defaultMapping);
    }
  };

  const hrSystemFields = [
    { key: 'employee_id', label: 'Employee ID', required: true },
    { key: 'full_name', label: 'Full Name', required: true },
    { key: 'email', label: 'Email Address', required: false },
    { key: 'phone', label: 'Phone Number', required: false },
    { key: 'department', label: 'Department', required: true },
    { key: 'position', label: 'Position/Job Title', required: true },
    { key: 'salary', label: 'Salary/Wage', required: true },
    { key: 'start_date', label: 'Employment Start Date', required: true },
    { key: 'end_date', label: 'Employment End Date', required: false },
    { key: 'employment_type', label: 'Employment Type', required: true },
    { key: 'tax_file_number', label: 'Tax File Number', required: true },
    { key: 'superannuation_fund', label: 'Superannuation Fund', required: false },
    { key: 'bank_account', label: 'Bank Account Number', required: false },
    { key: 'address', label: 'Residential Address', required: false }
  ];

  const selectedIntegrationData = integrations?.find(i => i?.id === selectedIntegration);
  const system = erpSystems?.find(s => s?.id === selectedIntegrationData?.erp_system_id);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Field Mapping Configuration</h2>
            <p className="text-sm text-gray-600 mt-1">
              Map HR system fields to your ERP system's data structure
            </p>
          </div>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!selectedIntegration}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Mappings
          </Button>
        </div>
      </div>
      <div className="p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select ERP Integration
          </label>
          <Select
            value={selectedIntegration}
            onChange={(e) => handleIntegrationChange(e?.target?.value)}
          >
            <option value="">Choose an integration</option>
            {integrations?.map((integration) => (
              <option key={integration?.id} value={integration?.id}>
                {integration?.erp_system_name}
              </option>
            ))}
          </Select>
        </div>

        {selectedIntegration ? (
          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>ERP System:</strong> {system?.name}
              </p>
              <p className="text-sm text-blue-800 mt-1">
                Configure how your HR system fields map to {system?.name} field names
              </p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 pb-3 border-b border-gray-200 font-medium text-sm text-gray-700">
                <div>HR System Field</div>
                <div>Maps To</div>
                <div>ERP Field Name</div>
              </div>

              {hrSystemFields?.map((field) => (
                <div key={field?.key} className="grid grid-cols-3 gap-4 items-center">
                  <div>
                    <span className="text-sm text-gray-900">{field?.label}</span>
                    {field?.required && (
                      <span className="text-red-600 ml-1">*</span>
                    )}
                  </div>
                  <div className="flex justify-center">
                    <span className="text-gray-400">→</span>
                  </div>
                  <div>
                    <input
                      type="text"
                      value={mappings?.[field?.key] || ''}
                      onChange={(e) => setMappings({ ...mappings, [field?.key]: e?.target?.value })}
                      placeholder={`Enter ${system?.name} field name`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Mapping Preview</h4>
              <pre className="text-xs text-gray-700 overflow-x-auto">
                {JSON.stringify(mappings, null, 2)}
              </pre>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Select an ERP integration to configure field mappings</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldMappingEditor;